import { DataProvider, Datum, DatumReader, MetaData, SpatialBounds, TemporalRange, AnimationMode } from './types';
import { TimelineRange, CursorMillis } from '../timeline/timeline-common';
import { Observable } from 'rxjs';


export interface AnimationDistribution extends CursorMillis {
	under: number[],
	earlier: number[],
	later: number[]
}

export type FilterOp<T> = (filterValue: T, datum: Datum) => boolean

class Filter<T> {

	constructor(private filterOperation?: FilterOp<T>, private filterValue?: T) {
		if (this.filterOperation === undefined) {
			this.filterOperation = (filterValue_: T, datum_: Datum) => true; //all passes
		}
	}

	public filter(datum: Datum): boolean {
		return this.filterOperation(this.filterValue, datum);
	}

	public setFilterValue(filterValue: T): Filter<T> {
		this.filterValue = filterValue;
		return this;
	}

	public getFilterValue(): T {
		return this.filterValue;
	}

	public setFilterOperation(filterOperation: FilterOp<T>): Filter<T> {
		this.filterOperation = filterOperation;
		if (this.filterOperation === undefined) {
			this.filterOperation = (filterValue_: T, datum_: Datum) => true; //all passes
		}
		return this;
	}

}


export class DataList implements DataProvider {
	public static FILTER_DELETED = 'deleted';
	public static FILTER_SEARCH = 'search';

	private data: Datum[];
	private dataIdIndexMap: Map<string, number>;
	private meta: MetaData;
	private animationInterval: number;
	private animationKeys: number[];
	private animationDistribution: { [key: number]: AnimationDistribution };
	private filters: { [key: string]: Filter<any> };
	private filteredIndices: number[];
	private filteredData: Datum[];
	private selectionLocked: boolean;

	private metaObservable: Observable<MetaData>;
	private notifyNewMeta: () => void


	public constructor(private type: string, private datumReader: DatumReader, data?: Datum[]) {
		this.data = data != null ? data : [];
		this.dataIdIndexMap = new Map();
		this.meta = {
			start: undefined,
			end: undefined,
			north: undefined,
			east: undefined,
			south: undefined,
			west: undefined,
			version: undefined,
			lastUpdate: Date.now(),
			selected: []
		};
		this.animationDistribution = [];
		this.filters = {};
		this.filters[DataList.FILTER_DELETED] = new Filter<boolean>(this.createDeletionFilterOperation(), false);
		this.filters[DataList.FILTER_SEARCH] = new Filter<string>(undefined, '');
		this.filteredIndices = undefined;
		this.filteredData = [];
		this.selectionLocked = false;
		this.notifyNewMeta = () => { };

		let self = this;
		this.metaObservable = new Observable<MetaData>(subscriber => {
			self.notifyNewMeta = () => {
				try {
					subscriber.next(self.getMeta());
				} catch (err) {
					subscriber.error(err); // delivers an error if it caught one
				}
			}
		});


		if (data != null) {
			this.applyFilter();
		}
	}

	public getMetaObservable(): Observable<MetaData> {
		return this.metaObservable;
	}

	public getType(): string {
		return this.type;
	}

	public lockSelection(lock: boolean): void {
		this.selectionLocked = lock;
	}

	public isSelectionLocked(): boolean {
		return this.selectionLocked;
	}

/**
		 * This method gets called when the user deleted the given datum and the server returned 
		 * successfully after the deletion. A call to this method reaplies the filters and select the datum 
		 * referenced by  given autoSelectOnDeletionId. 
		 */
	public handleDatumDelete(_datum: Datum, autoSelectOnDeletionId: string) {
		this.handleDeletedDataSelection(autoSelectOnDeletionId);
		this.applyFilter();
	}

	private updateMeta(updateTimestamp?: number): DataList {
		let data = this.getFilteredData();
		this.dataIdIndexMap = updateMeta(this.meta, data, this.datumReader, updateTimestamp);

		if (updateTimestamp) {
			this.notifyNewMeta();
		}

		return this;
	}

	public getRange(): TemporalRange {
		return this.meta;
	}

	public getSelectionRange(): TemporalRange {
		if (this.meta.selected.length > 0) {
			let selection: Datum[] = this.getSelection();
			let datum = selection[0];
			let dataIsSpatial = this.datumReader.isSpatial(datum);
			if (dataIsSpatial) {
				let range: TemporalRange = {
					end: this.datumReader.getEnd(datum),
					start: this.datumReader.getStart(datum)
				}
				for (let i = 1; i < selection.length; i++) {
					datum = selection[i];
					range.start = Math.min(range.start, this.datumReader.getStart(datum));
					range.end = Math.max(range.end, this.datumReader.getEnd(datum));
				}
				return range;
			}
		}
		return undefined;
	}

	public getBounds(): SpatialBounds {
		return this.meta;
	}

	public getSelectionBounds(): SpatialBounds {
		if (this.meta.selected.length > 0) {
			let selection: Datum[] = this.getSelection();
			let datum = selection[0];
			let dataIsSpatial = this.datumReader.isSpatial(datum);
			if (dataIsSpatial) {
				let bounds: SpatialBounds = {
					north: this.datumReader.getNorth(datum),
					south: this.datumReader.getSouth(datum),
					east: this.datumReader.getEast(datum),
					west: this.datumReader.getWest(datum)
				}
				for (let i = 1; i < selection.length; i++) {
					datum = selection[i];
					bounds.south = Math.min(bounds.south, this.datumReader.getSouth(datum));
					bounds.north = Math.max(bounds.north, this.datumReader.getNorth(datum));
					bounds.west = Math.min(bounds.west, this.datumReader.getWest(datum));
					bounds.east = Math.max(bounds.east, this.datumReader.getEast(datum));
				}
				return bounds;
			}
		}
		return undefined;
	}

	public clear(): Datum[] {
		let removedData = this.data.splice(0, this.data.length);// clear data items
		this.applyFilter(Date.now());
		return removedData;
	}

	public getMeta(): MetaData {
		return this.meta;
	}

	/**
	* Override when datum has different deleted property implementation
	*/
	protected createDeletionFilterOperation(): FilterOp<boolean> {
		return (deletionFilterValue: boolean, datum: Datum) => {
			if (deletionFilterValue === undefined) {
				return true
			} else if (deletionFilterValue === true) {
				return datum.deleted === true
			} else {
				return datum.deleted !== true
			}
		}
	}

	public setDeletionFilterOperation(filterOp: FilterOp<boolean>) {
		this.filters[DataList.FILTER_DELETED].setFilterOperation(filterOp);
		this.applyFilter();
	}
	public setDeletionFilterValue(filterValue: boolean) {
		this.filters[DataList.FILTER_DELETED].setFilterValue(filterValue);
		this.applyFilter();
	}
	public getDeletionFilterValue(): boolean {
		return this.filters[DataList.FILTER_DELETED].getFilterValue();
	}

	public setSearchFilter(filterOp: FilterOp<string>) {
		this.filters[DataList.FILTER_SEARCH].setFilterOperation(filterOp);
		this.applyFilter();
	}

	public setSearchTerm(searchTerm: string) {
		this.filters[DataList.FILTER_SEARCH].setFilterValue(searchTerm);
		this.applyFilter();
	}

	public getSearchTerm(): string {
		return this.filters[DataList.FILTER_SEARCH].getFilterValue();
	}

	private filter(datum: Datum): boolean {
		let pass = true;
		let filterNames = Object.keys(this.filters);
		let i = 0;
		while (i < filterNames.length && pass) {
			pass = this.filters[filterNames[i]].filter(datum);
			i++;
		}
		return pass;
	}

	public setSelection(selectedIds: string | string[]): string[] {
		if (!this.isSelectionLocked()) {
			let ids = selectedIds ? (<string>selectedIds).substring ? [<string>selectedIds] : <string[]>selectedIds : [];
			this.meta.selected = this.filteredData.filter(datum => ids.includes(datum.id)).map(datum => datum.id);
		}
		return this.meta.selected;
	}

	public getSize(): number {
		return this.filteredData ? this.filteredData.length : 0;
	}

	public getSelectionSize(): number {
		return this.meta.selected.length;
	}

	public inBounds(index: number): boolean {
		return this.data != null && index >= 0 && index < this.filteredData.length;
	}
	public get(index: number | string): Datum {
		//index might also be an id
		if ((<string>index).substring) {
			let _index: number = this.dataIdIndexMap.get(<string>index);
			return (_index != null ? this.filteredData[_index] : null);
		} else {
			return this.filteredData[<number>index];
		}
	}

	public getSelection(): Datum[] {
		return this.meta.selected
			.map(id => this.dataIdIndexMap.get(id))
			.filter(index => index != null)
			.map(index => this.filteredData[index]);
	}
	public getSelectionIds(): string[] {
		return this.meta.selected;
	}

	public getDataIds(): string[] {
		return Array.from(this.dataIdIndexMap.keys());
	}

	public getData(animationPosition?: CursorMillis, animationMode?: AnimationMode): Datum[] {

		let animationIndexFilter: (datum: Datum, index: number) => boolean = undefined;
		let animationDatumFilter: (datum: Datum) => boolean = undefined;
		if (animationPosition !== undefined && animationMode !== undefined && animationMode !== AnimationMode.always) {
			if (this.animationDistribution[animationPosition.center] !== undefined) {
				animationIndexFilter = (datum: Datum, index: number) => (<number[]>(<any>this.animationDistribution[animationPosition.center])[AnimationMode[animationMode]]).includes(index);
			} else {
				animationDatumFilter = (datum: Datum) => <boolean>(<any>getAnimationDistribution(animationPosition, datum, this.datumReader))[AnimationMode[animationMode]];
			}
		}
		return (animationIndexFilter
			? this.filteredData.filter(animationIndexFilter)
			: animationDatumFilter
				? this.filteredData.filter(animationDatumFilter)
				: this.filteredData);
	}

	public indexOf(id: string | Datum): number {
		//id can also be a datum
		var index = this.dataIdIndexMap.get((<string>id).substring ? <string>id : (<Datum>id).id);
		return index != null ? index : -1;
	}

	protected shouldBeReplaced(oldDatum: Datum, newDatum: Datum): boolean {
		let curVersion = this.datumReader.getVersion(oldDatum);
		if (curVersion == null || curVersion < this.datumReader.getVersion(newDatum)) {
			// the datum in this list is older so remove
			return true;
		} else {
			// datum in this feed is younger or same version
			return false;
		}
	}

	public mergeData(timeRange: TimelineRange, animationTiming: CursorMillis[], timestamp: number, dataToMerge?: DataList | any[]): boolean {

		let mergedData = this.removeData(timeRange).length > 0;

		let oldData = (dataToMerge != null ? this.data : []);
		let newData = (dataToMerge != null ? (<DataList>dataToMerge).mergeData ? (<DataList>dataToMerge).data : <any[]>dataToMerge : this.data);

		let updatedIds = [];

		let hasTimeSeries = (oldData.length > 0 && oldData[0].timeSeries !== undefined) || (newData.length > 0 && newData[0].timeSeries !== undefined);

		// compare for duplicates (same id) in this DataList and the dataListToMerge
		// and remove the oldest dataItem
		let datum;
		let curIndex;
		let dynamicDataItem;
		for (var i = 0; i < newData.length; i++) {
			datum = newData[i];
			curIndex = indexOf(datum.id, oldData);
			if (curIndex != -1) {// current dataItem is already in this feed
				if (this.shouldBeReplaced(oldData[curIndex], datum)) {
					oldData.splice(curIndex, 1);
					updatedIds.push(datum.id);
					mergedData = true;
				} else {
					if (hasTimeSeries) {// when we deal with time series data (e.g. sensors)
						// we need to merge this time series too
						dynamicDataItem = oldData[curIndex];
						mergedData = mergedData || dynamicDataItem.timeSeries.mergeData(timeRange, animationTiming, datum.timeSeries);
					}
					newData.splice(i, 1);
					i--;
				}
				//				let curVersion = this.datumReader.getVersion(oldData[curIndex]);
				//				if (curVersion == null || curVersion < this.datumReader.getVersion(datum)) {
				//					// the datum in this list is older so remove
				//					oldData.splice(curIndex, 1);
				//					updatedIds.push(datum.id);
				//					mergedData = true;
				//				} else {// datum in this feed is younger// or same version
				//					if (this.shouldBeReplaced(oldData, datum)) {
				//						oldData.splice(curIndex, 1);
				//						updatedIds.push(datum.id);
				//						mergedData = true;
				//
				//					} else {
				//
				//						if (hasTimeSeries) {// when we deal with time series data (e.g. sensors)
				//							// we need to merge this time series too
				//							dynamicDataItem = oldData[curIndex];
				//							mergedData = mergedData || dynamicDataItem.timeSeries.mergeData(timeRange, animationTiming, datum.timeSeries);
				//						}
				//						newData.splice(i, 1);
				//						i--;
				//					}
				//				}
			}
		}
		mergedData = mergedData || (dataToMerge != null && newData.length > 0);
		// now merge the two arrays.
		let isAnUpdate, isNew, index;
		let ni = 0;
		let oi = 0;
		while (ni < newData.length && oi < oldData.length) {
			index = oi;
			if (this.datumReader.compareByTime(oldData[oi], newData[ni]) <= 0) {
				// old data item first so just advance
				oi++;
				isAnUpdate = false;
				isNew = false;
			} else {
				// new data item first so insert into dataList
				oldData.splice(oi++, 0, newData[ni++]);
				isAnUpdate = (updatedIds.indexOf(oldData[index].id) != -1);
				isNew = !isAnUpdate;

				if (hasTimeSeries) {// when we deal with time series data (e.g. sensors)
					// we need to merge this time series too
					dynamicDataItem = oldData[index];
					dynamicDataItem.timeSeries.mergeData(timeRange, animationTiming, timestamp);
				}
			}

			datum = oldData[index];
		}

		while (oi < oldData.length) {
			index = oi;
			datum = oldData[index];
			oi++;
		}

		index = oldData.length;
		while (ni < newData.length) {
			oldData.push(newData[ni++]);
			datum = oldData[index];
			isAnUpdate = (updatedIds.indexOf(datum.id) != -1);

			if (hasTimeSeries) {// when we deal with time series data (e.g. sensors)
				// we need to merge this time series too
				dynamicDataItem = oldData[index];
				dynamicDataItem.timeSeries.mergeData(timeRange, animationTiming, timestamp);
			}

			index++;
		}

		this.data = oldData;

		this.animationInterval = animationTiming.length > 1 ? animationTiming[1].center - animationTiming[0].center : undefined;

		this.animationKeys = animationTiming.map(cursorMillis => cursorMillis.center);


		this.animationDistribution = createBlankAnimationDistribution(animationTiming);

		this.applyFilter(mergedData ? timestamp : undefined);

		return mergedData;
	}

	/**
	* Removes all data from this list for given time range 
	 */
	private removeData(timeRange: TimelineRange): Datum[] {
		var dataItemsRemoved = [];
		var holdsTimeSeries = (this.data.length > 0 && this.data[0].timeSeries !== undefined);
		for (let i = 0; i < this.data.length; i++) {
			let datum = this.data[i];

			let inRange = !timeRange || timeRange.visible.overlap(datum);

			if (inRange) {
				//this datum is in range and should stay. But we have to trim its time series data as well
				if (holdsTimeSeries) {
					if (datum.timeSeries.getSize() > 0) {
						datum.timeSeries.removeData(timeRange);
					}
				}
			} else {
				//datum false outside requested range and should be removed
				dataItemsRemoved.push(this.data[i]);
				let selectedIndex = this.meta.selected.indexOf(this.data[i].id);
				if (selectedIndex !== -1) {
					this.meta.selected.splice(selectedIndex, 1);
				}
				this.data.splice(i--, 1);
			}

		}

		return dataItemsRemoved;
	}

	/**
	* Modify selected data that became deleted when deleted data is filtered out. It flags the datum with given id to be selected when the UI  receives an update.  
	 */
	private handleDeletedDataSelection(autoSelectOnDeletionId?: string): void {
		if (this.getDeletionFilterValue() === false) {
			//make sure that the given datum id gets selected
			if (autoSelectOnDeletionId) {
				let selectedIndex = this.meta.selected.indexOf(autoSelectOnDeletionId);
				if (selectedIndex == -1) {
					this.meta.selected.push(autoSelectOnDeletionId);
				}
			}

			for (let i = 0; i < this.data.length; i++) {
				let datum = this.data[i];
				if (datum.deleted) {
					let selectedIndex = this.meta.selected.indexOf(this.data[i].id);
					if (selectedIndex !== -1) {
						this.meta.selected.splice(selectedIndex, 1);
					}
				}
			}
		}
	}

	public applyFilter(updateTimestamp?: number): number[] {
		if (this.data && this.data.length > 0) {
			this.filteredIndices = this.data
				.map((datum, index) => { return { datum: datum, index: index } })
				.filter(di => this.filter(di.datum))
				.map(di => di.index);
		} else {
			this.filteredIndices = [];
		}
		
		this.filteredData = this.filteredIndices ? this.filteredIndices.map(index => this.data[index]) : this.data;

		applyDataToAnimationDistribution(this.animationDistribution, this.filteredData, this.datumReader);

		this.updateMeta(updateTimestamp);

		return this.filteredIndices;
	}

	protected getFilteredData(): Datum[] {
		return this.filteredData;
	}

	public getAnimationInterval(): number {
		return this.animationInterval;
	}

	public getAnimationKeys(): number[] {
		return this.animationKeys;
	}

	public getAnimationDistribution(): { [key: number]: AnimationDistribution } {
		return this.animationDistribution;
	}


}

const indexOf = (id: string, dataArray: Datum[]): number => {
	if (dataArray != null && dataArray.length > 0) {
		for (let i = 0; i < dataArray.length; i++) {
			if (dataArray[i].id === id) return i;
		}
	}
	return -1;
};

export const updateMeta = (meta: MetaData, data: Datum[], datumReader: DatumReader, updateTimestamp?: number): Map<string, number> => {
	let dataIdIndexMap: Map<string, number>;
	dataIdIndexMap = new Map();
	if (data.length > 0) {
		let datum = data[0];

		dataIdIndexMap.set(datum.id, 0);

		let dataIsTemporal = datumReader.isTemporal(datum);
		let dataIsSpatial = datumReader.isSpatial(datum);
		let dataIsVersional = datumReader.isVersional(datum);

		if (dataIsTemporal) {
			meta.start = datumReader.getStart(datum);
			meta.end = datumReader.getEnd(datum);
		}
		if (dataIsSpatial) {
			meta.north = datumReader.getNorth(datum);
			meta.south = datumReader.getSouth(datum);
			meta.east = datumReader.getEast(datum);
			meta.west = datumReader.getWest(datum);
		}
		if (dataIsVersional) {
			meta.version = datumReader.getVersion(datum);
		}
		for (let i = 1; i < data.length; i++) {
			datum = data[i];
			dataIdIndexMap.set(datum.id, i);
			if (dataIsVersional) {
				meta.version = Math.max(meta.version, datumReader.getVersion(datum));
			}
			if (dataIsSpatial) {
				meta.south = Math.min(meta.south, datumReader.getSouth(datum));
				meta.north = Math.max(meta.north, datumReader.getNorth(datum));
				meta.west = Math.min(meta.west, datumReader.getWest(datum));
				meta.east = Math.max(meta.east, datumReader.getEast(datum));
			}
			if (dataIsTemporal) {
				meta.start = Math.min(meta.start, datumReader.getStart(datum));
				meta.end = Math.max(meta.end, datumReader.getEnd(datum));
			}
		}
		let selected = data.filter(datum => meta.selected.includes(datum.id)).map(datum => datum.id);
		meta.selected = selected;

	} else {
		meta.start = null;
		meta.end = null;
		meta.version = null;
		meta.north = null;
		meta.east = null;
		meta.south = null;
		meta.west = null;
		meta.selected = [];
	}
	meta.lastUpdate = updateTimestamp != undefined ? updateTimestamp : meta.lastUpdate;

	return dataIdIndexMap;
}

export const createBlankAnimationDistribution = (animationTiming: CursorMillis[] | { [key: number]: AnimationDistribution }): { [key: number]: AnimationDistribution } => {
	if (animationTiming) {
		if ((<CursorMillis[]>animationTiming).push) {
			return (<CursorMillis[]>animationTiming)
				.map(cursorMillis => Object.assign({ under: undefined, earlier: undefined, later: undefined }, cursorMillis))
				.reduce((map, animationDistribution) => {
					map[animationDistribution.center] = animationDistribution;
					return map;
				}, <{ [key: number]: AnimationDistribution }>{});
		} else {
			let sourceDistribution = <{ [key: number]: AnimationDistribution }>animationTiming;
			return Object.values(sourceDistribution)
				.map((distribution) => Object.assign({}, distribution, { under: undefined, earlier: undefined, later: undefined }))
				.reduce((map, distribution) => {
					map[distribution.center] = distribution;
					return map;
				}, <{ [key: number]: AnimationDistribution }>{});
		}
	}
	return {};
};
export const applyDataToAnimationDistribution = (animationDistribution: { [key: number]: AnimationDistribution }, data: Datum[], datumReader: DatumReader): { [key: number]: AnimationDistribution } => {

	let animationDistributionList = Object.values(animationDistribution).sort((ad0, ad1) => ad0.center - ad1.center);

	animationDistributionList.forEach(ad => {
		ad.earlier = [];
		ad.under = [];
		ad.later = [];
	});

	data.forEach((datum, index) => {
		storeInAnimationDistribution(animationDistributionList, datum, index, datumReader);
	});
	return animationDistribution;
}
export const getAnimationDistribution = (animationTimingEntry: CursorMillis, datum: Datum, datumReader: DatumReader): { later: boolean, under: boolean, earlier: boolean } => {
	if (datumReader.isTemporal(datum)) {
		if (datumReader.isMoment(datum)) {
			let time = datumReader.getTime(datum);
			return {
				later: animationTimingEntry.center < time,
				under: animationTimingEntry.end >= time && animationTimingEntry.start <= time,
				earlier: animationTimingEntry.center >= time
			};
		} else if (datumReader.isRange(datum)) {
			let start = datumReader.getStart(datum);
			let end = datumReader.getEnd(datum);
			return {
				later: animationTimingEntry.center < start,
				under: animationTimingEntry.center < end && animationTimingEntry.center >= start,
				earlier: animationTimingEntry.center < end
			};
		} else if (datumReader.isPerpetual(datum)) {
			return {
				later: false,
				under: true,
				earlier: false
			};
		} else if (datumReader.isSince(datum)) {
			let start = datumReader.getStart(datum);
			return {
				later: animationTimingEntry.center < start,
				under: animationTimingEntry.center >= start,
				earlier: animationTimingEntry.center >= start
			};
		} else {
			let end = datumReader.getEnd(datum);
			return {
				later: false,
				under: animationTimingEntry.center < end,
				earlier: true
			};
		}
	} else {
		return {
			later: true,
			under: true,
			earlier: true
		};
	}
}
const storeInAnimationDistribution = (animationDistribution: AnimationDistribution[], datum: Datum, datumIndex: number, datumReader: DatumReader): void => {
	if (datumReader.isTemporal(datum)) {
		if (datumReader.isMoment(datum)) {
			storeMomentInAnimationDistribution(animationDistribution, datumReader.getTime(datum), datumIndex);
		} else if (datumReader.isRange(datum)) {
			storeRangeInAnimationDistribution(animationDistribution, datumReader.getStart(datum), datumReader.getEnd(datum), datumIndex);
		} else if (datumReader.isPerpetual(datum)) {
			storePerpetualInAnimationDistribution(animationDistribution, datumIndex);
		} else if (datumReader.isSince(datum)) {
			storeSinceInAnimationDistribution(animationDistribution, datumReader.getStart(datum), datumIndex);
		} else {
			storeUntilInAnimationDistribution(animationDistribution, datumReader.getEnd(datum), datumIndex);
		}
	} else {
		storeNoneTemporalInAnimationDistribution(animationDistribution, datumIndex);
	}
}

const storeMomentInAnimationDistribution = (animationDistribution: AnimationDistribution[], time: number, datumIndex: number): void => {
	let index = 0;
	while (index < animationDistribution.length && animationDistribution[index].end < time) {
		animationDistribution[index].later.push(datumIndex);
		index++;
	}
	while (index < animationDistribution.length && animationDistribution[index].start <= time) {
		animationDistribution[index].under.push(datumIndex);
		if (animationDistribution[index].center < time) {
			animationDistribution[index].later.push(datumIndex);
		} else {
			animationDistribution[index].earlier.push(datumIndex);
		}
		index++;
	}
	while (index < animationDistribution.length) {
		animationDistribution[index].earlier.push(datumIndex);
		index++;
	}
}
const storeRangeInAnimationDistribution = (animationDistribution: AnimationDistribution[], start: number, end: number, datumIndex: number): void => {
	let index = 0;
	while (index < animationDistribution.length && animationDistribution[index].center < start) {
		animationDistribution[index].later.push(datumIndex);
		index++;
	}
	while (index < animationDistribution.length && animationDistribution[index].center < end) {
		animationDistribution[index].under.push(datumIndex);
		animationDistribution[index].earlier.push(datumIndex);
		index++;
	}
	while (index < animationDistribution.length) {
		animationDistribution[index].earlier.push(datumIndex);
		index++;
	}
}
const storePerpetualInAnimationDistribution = (animationDistribution: AnimationDistribution[], datumIndex: number): void => {
	let index = 0;
	while (index < animationDistribution.length) {
		animationDistribution[index].under.push(datumIndex);
		index++;
	}
}
const storeSinceInAnimationDistribution = (animationDistribution: AnimationDistribution[], start: number, datumIndex: number): void => {
	let index = 0;
	while (index < animationDistribution.length && animationDistribution[index].center < start) {
		animationDistribution[index].later.push(datumIndex);
		index++;
	}
	while (index < animationDistribution.length) {
		animationDistribution[index].under.push(datumIndex);
		animationDistribution[index].earlier.push(datumIndex);
		index++;
	}
}
const storeUntilInAnimationDistribution = (animationDistribution: AnimationDistribution[], end: number, datumIndex: number): void => {
	let index = 0;
	while (index < animationDistribution.length && animationDistribution[index].center < end) {
		animationDistribution[index].under.push(datumIndex);
		animationDistribution[index].earlier.push(datumIndex);
		index++;
	}
	while (index < animationDistribution.length) {
		animationDistribution[index].earlier.push(datumIndex);
		index++;
	}
}
const storeNoneTemporalInAnimationDistribution = (animationDistribution: AnimationDistribution[], datumIndex: number): void => {
	let index = 0;
	while (index < animationDistribution.length) {
		animationDistribution[index].later.push(datumIndex);
		animationDistribution[index].under.push(datumIndex);
		animationDistribution[index].earlier.push(datumIndex);
		index++;
	}
}
